import { Nullable } from '@ps/utils';
import {
  Survey,
  SurveyActivityLogEntry,
  SurveyComment,
  SurveyHealthMetric,
  SurveyQuota,
} from '@ps/survey-utils';

export interface ManageSurveyState {
  readonly survey: Nullable<Survey>;
  readonly childSurvey: Nullable<Survey>; // Selected child survey (MCQB/tracker/grouped) whose CPI the preview shows; null for single surveys.
  readonly childSurveyLoading: boolean;
  readonly childSurveyError: Nullable<string>;
  readonly loading: boolean;
  readonly error: Nullable<string>;
  readonly healthMetrics: SurveyHealthMetric[];
  readonly healthMetricsLoading: boolean;
  readonly healthMetricsError: Nullable<string>;
  readonly remainingHours: Nullable<number>;
  readonly remainingHoursLoading: boolean;
  readonly remainingHoursError: Nullable<string>;
  // Entries shown by the manage-survey Activity Logs drawer — not a separate
  // sub-state slice since it's just another array of objects for the same survey.
  readonly activityLogs: SurveyActivityLogEntry[];
  readonly activityLogsLoading: boolean;
  readonly activityLogsError: Nullable<string>;
  readonly comments: SurveyComment[];
  readonly commentsLoading: boolean;
  readonly commentsError: Nullable<string>;
  readonly addingComment: boolean;
  readonly addCommentError: Nullable<string>;
  readonly quotas: SurveyQuota[];
  readonly quotasLoading: boolean; // Initial loadSurveyQuotas is in flight; distinct from quotasLoadingMore (pagination).
  readonly quotaTotal: number; // Total quota count received by the API; drives the "Show all Quotas (N)" footer.
  readonly quotaSearch: string; // Current search term, resent by loadMoreQuotas/loadAllQuotas so pagination stays filtered.
  readonly quotaLoadedPage: number; // Highest page number already accumulated into `quotas` (0 = none loaded yet).
  readonly quotasLoadingMore: boolean; // A "Show next/all" load is in flight;
  readonly quotaLockingIds: string[];
  readonly quotaSavingIds: string[];
  readonly quotaCurrentlyOpenSavingIds: string[];
  // Composite `${psQuotaId}:${supplierRefId}` keys — see supplierQuotaKey().
  readonly supplierQuotaSavingIds: string[];
  readonly supplierQuotaCurrentlyOpenSavingIds: string[];
  readonly quotaRenamingIds: string[];
  readonly quotaCpiSavingIds: string[];
  readonly trafficChannels: Survey[];
  readonly trafficChannelsLoading: boolean;
  readonly trafficChannelsError: Nullable<string>;
  readonly tcStatusChangingIds: number[];
  readonly creatingTrafficChannel: boolean;
  readonly trafficChannelCreationError: Nullable<string>;
  readonly psidUploadLoading: boolean;
  readonly psidUploadError: Nullable<string>;
  readonly psidUploadFilePath: Nullable<string>;
  readonly psidUploadUniqCount: Nullable<number>;
  readonly psidUploadTotalCount: Nullable<number>;
  // Links & Other Details drawer — operator tools. Same rationale as
  // activityLogs/remainingHours above: plain fields on the same survey, not a
  // separate sub-state slice. Fusion block state lives in
  // @ps/survey-management-data-access's surveyManagement feature instead.
  readonly crmIdSaving: boolean;
  readonly crmIdError: Nullable<string>;
  readonly asanaIdSaving: boolean;
  readonly asanaIdError: Nullable<string>;
  readonly billingNumberSaving: boolean;
  readonly billingNumberError: Nullable<string>;
  readonly customRedirectFieldSaving: boolean;
  readonly customRedirectFieldError: Nullable<string>;
}

export const INITIAL_MANAGE_SURVEY_STATE: ManageSurveyState = {
  survey: null,
  childSurvey: null,
  childSurveyLoading: false,
  childSurveyError: null,
  loading: false,
  error: null,
  healthMetrics: [],
  healthMetricsLoading: false,
  healthMetricsError: null,
  remainingHours: null,
  remainingHoursLoading: false,
  remainingHoursError: null,
  activityLogs: [],
  activityLogsLoading: false,
  activityLogsError: null,
  comments: [],
  commentsLoading: false,
  commentsError: null,
  addingComment: false,
  addCommentError: null,
  quotas: [],
  quotasLoading: false,
  quotaTotal: 0,
  quotaSearch: '',
  quotaLoadedPage: 0,
  quotasLoadingMore: false,
  quotaLockingIds: [],
  quotaSavingIds: [],
  quotaCurrentlyOpenSavingIds: [],
  supplierQuotaSavingIds: [],
  supplierQuotaCurrentlyOpenSavingIds: [],
  quotaRenamingIds: [],
  quotaCpiSavingIds: [],
  trafficChannels: [],
  trafficChannelsLoading: false,
  trafficChannelsError: null,
  tcStatusChangingIds: [],
  creatingTrafficChannel: false,
  trafficChannelCreationError: null,
  psidUploadLoading: false,
  psidUploadError: null,
  psidUploadFilePath: null,
  psidUploadUniqCount: null,
  psidUploadTotalCount: null,
  crmIdSaving: false,
  crmIdError: null,
  asanaIdSaving: false,
  asanaIdError: null,
  billingNumberSaving: false,
  billingNumberError: null,
  customRedirectFieldSaving: false,
  customRedirectFieldError: null,
};
